/* Unified meta-classifier interface for the frontend.
 *
 * Two implementations behind one shape:
 *   - Mock: wraps the regex `detectMilestones()` from ./detector. Pure,
 *     deterministic, no API key required, used in dev + tests.
 *   - Live: POSTs to /api/classify (the Express server in `server/`)
 *     which calls OpenAI with the meta-prompt + structured outputs.
 *
 * The mode is resolved by the same loaderMode() that picks the avatar
 * implementation, so `?cb=live` flips both at once.
 *
 * Live cardinality: at most one milestone per turn (OpenAI returns
 * one classification).
 * Mock cardinality: 0..N per turn (the regex detector can match
 * multiple rules on the same producer turn). */

import { loaderMode, type PersonaId } from '@/lib/codebaby';
import { detectMilestones } from './detector';
import { defaultTemperatureDelta } from './milestones';
import type { MilestoneEvent, MilestoneTag, MomentType } from './types';

export interface ClassifyTurnArgs {
  personaId: PersonaId;
  producerText: string;
  personaText: string;
  alreadyHit: MilestoneTag[];
  /** Stable id of the producer turn this classification is attached to.
   *  Used to quote the turn back in the debrief. */
  turnId: string;
  /** Ordinal index in the session — used by the live classifier for
   *  prompt context. */
  turnIndex: number;
}

export interface ClassifyTurnResult {
  milestones: MilestoneEvent[];
  /** Signed temperature delta to apply to the gauge for this turn. */
  temperatureDelta: number;
}

export interface ClassifierClient {
  classifyTurn(args: ClassifyTurnArgs): Promise<ClassifyTurnResult>;
}

// ----- Mock implementation ------------------------------------------------

export function createMockClassifier(): ClassifierClient {
  return {
    async classifyTurn(args) {
      const milestones = detectMilestones({
        personaId: args.personaId,
        producerText: args.producerText,
        personaText: args.personaText,
        turnId: args.turnId,
        alreadyHit: args.alreadyHit,
      });
      const temperatureDelta = milestones.reduce(
        (sum, m) => sum + defaultTemperatureDelta(m.tag),
        0,
      );
      return { milestones, temperatureDelta };
    },
  };
}

// ----- Live implementation ------------------------------------------------

export interface LiveClassifierConfig {
  baseUrl: string;
  apiKey: string;
  /** Injected for tests. */
  fetchImpl?: typeof fetch;
}

interface LiveApiResponse {
  classification: {
    temperature_delta: number;
    is_key_moment: boolean;
    moment_type: MomentType;
    milestone_tag: string | null;
    objection_label: string | null;
    yield_quality: 'earned' | 'hollow' | null;
    notes: string;
  };
  elapsedMs: number;
}

export function createLiveClassifier(config: LiveClassifierConfig): ClassifierClient {
  const fetchImpl = config.fetchImpl ?? fetch;
  return {
    async classifyTurn(args) {
      const url = `${config.baseUrl}/api/classify`;
      let res: Response;
      try {
        res = await fetchImpl(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': config.apiKey,
          },
          body: JSON.stringify({
            personaId: args.personaId,
            producerText: args.producerText,
            personaText: args.personaText,
            alreadyHit: args.alreadyHit,
            turnIndex: args.turnIndex,
          }),
        });
      } catch (err) {
        console.warn('[classifier] fetch FAILED (network/CORS)', err);
        throw err;
      }
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.warn(`[classifier] HTTP ${res.status}`, text.slice(0, 200));
        throw new Error(`Classifier returned ${res.status}: ${text || res.statusText}`);
      }
      const json = (await res.json()) as LiveApiResponse;
      const c = json.classification;

      const milestones: MilestoneEvent[] = c.milestone_tag
        ? [
            {
              tag: c.milestone_tag as MilestoneTag,
              turnId: args.turnId,
              producerText: args.producerText,
              isKeyMoment: c.is_key_moment,
              momentType: c.moment_type,
              notes: c.notes,
            },
          ]
        : [];

      return {
        milestones,
        temperatureDelta: c.temperature_delta,
      };
    },
  };
}

// ----- Dispatch -----------------------------------------------------------

let missingKeyWarned = false;

/** Build a classifier appropriate for the current loader mode. */
export function getClassifier(): ClassifierClient {
  if (loaderMode() === 'live') {
    const baseUrl = import.meta.env.VITE_CLASSIFIER_BASE_URL ?? 'http://localhost:8787';
    const apiKey = import.meta.env.VITE_CLASSIFIER_API_KEY ?? '';
    if (!apiKey) {
      if (!missingKeyWarned) {
        console.warn(
          '[classifier] live mode requested but VITE_CLASSIFIER_API_KEY is empty — ' +
            'the backend will reject every call. Falling back to mock for safety.',
        );
        missingKeyWarned = true;
      }
      return createMockClassifier();
    }
    return createLiveClassifier({ baseUrl, apiKey });
  }
  return createMockClassifier();
}
